'use client'

import { DopCollapse } from '@/components/template/DopCollapse'
import classNames from 'classnames'
import { useEffect } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { useFilterStore } from '@/store/useFilterStore'
import { MdOutlineDone } from 'react-icons/md'
import Tooltip from '@/components/ui/Tooltip'
import { startTransition } from 'react'
import { normalizeColor } from './utils/normalizeColor'


const COLORS_MAP: Record<string, string[]> = {
  черный: ['#000000'],
  белый: ['#FFFFFF'],

  темносерый: ['#555555'],
  синий: ['#0C3C78'],
  'синий/черный': ['#0C3C78', '#000000'],
  'красный/черный': ['#E74A2F', '#000000'],
  'фиолетовый/серый': ['#A5A5A5', '#555555'],
  'коричневый/черный': ['#8B4513', '#000000'],
  белыйсерый: ['#FFFFFF', '#888888'],
  серыйчерный: ['#888888', '#000000'],
  графитсерыйчерный: ['#555555', '#000000'],
  василек: ['#4A6EDB'],
  бордовый: ['#800020'],
  голубой: ['#5DADE2'],
  лайм: ['#32CD32'],
  лимонный: ['#F4D03F'],
  розовый: ['#C65967'],
  хаки: ['#4E5F3A'],
 'серый': ['#888888'],
  'темный меланж': ['#555555'],
  'желтый/черный': ['#FFFF00', '#000000'],
  'красный': ['#E74A2F'],
  'желтый': ['#FFFF00'],
  'хвойный': ['#3A454D'],
  'хвойный/черный': ['#3A454D','#000000'],
  'бежевый': ['#F5F5DC'],
  'белый/серый': ['#FFFFFF', '#888888'],
  'серый/черный': ['#888888', '#000000'],
  'графит серый/черный': ['#555555', '#000000'],
}

// Для красивых русских названий
const LABEL_MAP: Record<string, string> = {
  черный: 'Черный',
  белый: 'Белый',
  серый: 'Серый',
  темносерый: 'Темно-серый',
  синий: 'Синий',
  'синий/черный': 'Синий/Черный',
  'серый/черный': 'Серый/Черный',
  'красный/черный': 'Красный/Черный',
  'фиолетовый/серый': 'Фиолетовый/Серый',
  'коричневый/черный': 'Коричневый/Черный',
  бежевый: 'Бежевый',
  белыйсерый: 'Белый/Серый',
  серыйчерный: 'Серый/Черный',
  графитсерыйчерный: 'Графит/Серый',
  василек: 'Василек',
  бордовый: 'Бордовый',
  голубой: 'Голубой',
  лайм: 'Лайм',
  лимонный: 'Лимонный',
  розовый: 'Розовый',
  хаки: 'Хаки',
  желтый: 'Желтый',
  'желтый/черный': 'Желтый/Черный',
  красный: 'Красный'
}

interface ColorOption {
  label: string
  colors: string[]
}

interface ColorOptionsProps {
  colorOptions?: (string | null)[]
}

export const ColorLikeSize = ({ colorOptions }: ColorOptionsProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const selectedColors = useFilterStore((s) => s.selectedColors)
  const setColors = useFilterStore((s) => s.setColors)

  //удаление дублей
  const uniqueColors = Array.from(
    new Set(
      (colorOptions ?? [])
        .filter((c): c is string => Boolean(c))
        .map(normalizeColor)
        .filter((c): c is string => !!c && !!COLORS_MAP[c])
    )
  )

  // Сортировка: белый и черный вверх
  uniqueColors.sort((a, b) => {
    if (a === 'белый') return -1
    if (b === 'белый') return 1
    if (a === 'черный') return -1
    if (b === 'черный') return 1
    return 0
  })

  const mappedColors: ColorOption[] = uniqueColors.map((c) => ({
    label: LABEL_MAP[c] ?? c,
    colors: COLORS_MAP[c]!,
  }))


  useEffect(() => {
    const fromUrl = searchParams.get('color')
    if (fromUrl && fromUrl !== selectedColors) setColors(fromUrl)
  }, [])

  const updateUrl = (next: string | null) => {
    const params = new URLSearchParams(searchParams.toString())

    if (next) params.set('color', next)
    else params.delete('color')

    if (params.has('page')) params.set('page', '1')

    startTransition(() => {
      router.replace(`${pathname}?${params.toString()}`, { scroll: false })
    })
  }

  const handleClick = (label: string) => {
    const next = label === selectedColors ? null : label
    setColors(next)
    updateUrl(next)
  }

  return (
    <DopCollapse title="Цвет" defaultOpen>
      <div className="flex gap-3 flex-wrap">
        {mappedColors.map((color) => {
          const isActive = selectedColors === color.label

          const style =
            color.colors.length === 1
              ? {
                  backgroundColor: color.colors[0],
                  border:
                    color.colors[0] === '#FFFFFF'
                      ? '1px solid #ccc'
                      : `1px solid ${color.colors[0]}`,
                }
              : {
                  background: `linear-gradient(135deg, ${color.colors[0]} 50%, ${color.colors[1]} 50%)`,
                  border: `1px solid ${color.colors[0]}`,
                }

          return (
            <Tooltip key={color.label} title={color.label}>
              <button
                onClick={() => handleClick(color.label)}
                className={classNames(
                  'relative rounded-full border w-6 h-6 transition-all flex items-center justify-center',
                  'hover:scale-105'
                )}
                style={style}
              >
                {isActive && <MdOutlineDone size={18} className="text-white drop-shadow" />}
              </button>
            </Tooltip>
          )
        })}
      </div>
    </DopCollapse>
  )
}

// 'use client'
// import { DopCollapse } from '@/components/template/DopCollapse'
// import classNames from 'classnames'
// import { useEffect } from 'react'
// import { useSearchParams, useRouter, usePathname } from 'next/navigation'
// import { useFilterStore } from '@/store/useFilterStore'
// import { MdOutlineDone } from 'react-icons/md'
// import Tooltip from '@/components/ui/Tooltip'
//  import { startTransition } from 'react'
//  import { normalizeColor } from './utils/normalizeColor'

// interface ColorOption  {
//     label: string
//     colors: string[] 
// }

// const COLORS_MAP: Record<string, string[]> = {
//   'черный': ['#000000'],
//     'черные': ['#000000'],

//   'белый': ['#FFFFFF'],
//   'серый': ['#888888'],
//   'темный меланж': ['#555555'],
//   'синий': ['#0C3C78'],
//   'синий/черный': ['#0C3C78', '#000000'],
//   'красный/черный': ['#E74A2F', '#000000'],
//   'желтый/черный': ['#FFFF00', '#000000'],
//   'фиолетовый/серый': ['#A5A5A5', '#555555'],
//   'красный': ['#E74A2F'],
//   'желтый': ['#FFFF00'],
//   'хвойный': ['#3A454D'],
//   'хвойный/черный': ['#3A454D','#000000'],
//   'хаки': ['#4E5F3A'],
//   'розовый': ['#C65967'],
//   'бежевый': ['#F5F5DC'],
//   'белый/серый': ['#FFFFFF', '#888888'],
//   'серый/черный': ['#888888', '#000000'],
//   'графит серый/черный': ['#555555', '#000000'],
//   'коричневый/черный': ['#8B4513', '#000000'],
// }

// interface ColorOptionsProps {
//     colorOptions?: (string | null)[]
// }
// export const ColorLikeSize = ({ colorOptions }: ColorOptionsProps) => {
//     console.log('colorOptions from API:', colorOptions)
//     const router = useRouter()
//     const pathname = usePathname()
//     const searchParams = useSearchParams()

//     const selectedColors = useFilterStore((s) => s.selectedColors)
//     const setColors = useFilterStore((s) => s.setColors)


//     const mappedColors: ColorOption[] = (colorOptions ?? [])
//   .filter((c): c is string => Boolean(c))
//   .map((c) => {
//     const key = c.toLowerCase().trim()

//     return {
//       label: c,
//       colors: COLORS_MAP[key] ?? ['#000000'],
//     }
//   })


//     useEffect(() => {
//         const fromUrl = searchParams.get('color')
//         if (fromUrl && fromUrl !== selectedColors) setColors(fromUrl)
//     }, [])

//     const updateUrl = (next: string | null) => {
//         const params = new URLSearchParams(searchParams.toString())

//         if (next) params.set('color', next)
//         else params.delete('color')

//         if (params.has('page')) params.set('page', '1')

//         startTransition(() => {
//         router.replace(`${pathname}?${params.toString()}`, { scroll: false })
//     })
//     }

//     const handleClick = (label: string) => {
//         const next = label === selectedColors ? null : label

//         setColors(next)
//         updateUrl(next)
//     }

//     return (
//         <DopCollapse title="Цвет" defaultOpen>
//             <div className="flex gap-3 flex-wrap">
//                 {mappedColors.map((color) => {
//                     const isActive = selectedColors === color.label

//                     const style =
//                         color.colors.length === 1
//                           ? {
//         backgroundColor: color.colors[0],
//         border: color.colors[0] === '#FFFFFF'
//           ? '1px solid #ccc'
//           : `1px solid ${color.colors[0]}`
//       }
//     : {
//         background: `linear-gradient(135deg, ${color.colors[0]} 50%, ${color.colors[1]} 50%)`,
//         border: '1px solid #888'
//       }

//                     return (
//                         <Tooltip key={color.label} title={color.label}>
//                             <button
//                                 onClick={() => handleClick(color.label)}
//                                 className={classNames(
//                                     'relative rounded-full border w-6 h-6 transition-all flex items-center justify-center',
//                                     'hover:scale-105',
//                                 )}
//                                 style={style}
//                             >
//                                 {isActive && (
//                                     <MdOutlineDone
//                                         size={18}
//                                         className="text-white drop-shadow"
//                                     />
//                                 )}
//                             </button>
//                         </Tooltip>
//                     )
//                 })}
//             </div>
//         </DopCollapse>
//     )
// }
